import './ClientAnalytics.css';
import AnalyticsTable from './AnalyticsTable/AnalyticsTable';
import { Spinner } from 'react-bootstrap';
import * as chartAPIRequests from '../../shared/requests/teamAnalyticsChartRequests';
import { chartTypeEnum } from './constants';
import dayjs from '../../utils/dayjs';
import { Chart } from 'chart.js';
import { useDropdownTable } from '../../shared/hooks/useDropdownTable';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { teamAnalyticsUICopy, getTeamUICopyFieldsForItem } from './teamAnalyticsUICopy';
import { getBadgesForTeamAnalytics } from './teamAnalyticsBadges';

const DESCRIPTIVE_BADGE_LABELS = new Set([
  'status',
  'tone',
  'unit',
  'campaign',
  'team member',
  'topic',
  'experts',
  'outlets',
  'engagement log',
  'volume',
  'storytelling',
  'complexity',
  'type',
]);

const teamAnalyticsSections = [
  {
    title: 'Media Interactions Overview',
    description: 'How busy are we with Media Interactions, and what’s driving the mix?',
    chartQueryKeys: [
      'total-media-interactions',
      'team-driven-media-interactions',
      'proactive-client-media-interactions',
      'institutional-story-media-interactions',
    ],
  },
  {
    title: 'Issues & Risk Signals',
    description: 'Issue-related activity and signals that may need attention.',
    chartQueryKeys: ['issue-response-media-interactions', 'issues-complexity', 'tone-of-interactions'],
  },
  {
    title: 'Service Mix: Proactive vs Reactive',
    description: 'How much planned vs response Service work are we doing?',
    chartQueryKeys: ['proactive-services', 'total-reactive-services', 'proactive-media-relations-tactics'],
  },
  {
    title: 'Engagement Summary',
    description: 'What stakeholder Engagement activity is being logged.',
    chartQueryKeys: ['total-engagements', 'total-community-members-engaged'],
  },
  {
    title: 'Campaign Reporting',
    description: 'How activity breaks down across Campaigns.',
    chartQueryKeys: ['media-interactions-by-campaign', 'services-by-campaign', 'engagements-by-campaign'],
  },
];

// Alternative layouts for review:
// 1) "By Time Horizon": Daily Ops (volume/issues/proactive-reactive) then Executive Reporting (YoY/campaign/engagement).
// 2) "By Work Mode": Reactive (issue + reactive service) and Proactive (storytelling + tactics), then performance trend sections.

const teamSectionByQueryKey = teamAnalyticsSections.reduce((acc, section) => {
  section.chartQueryKeys.forEach((queryKey) => {
    acc[queryKey] = section.title;
  });
  return acc;
}, {});

const teamSectionDisplayOrder = teamAnalyticsSections.map(({ title, description }) => ({
  name: title,
  description,
}));

function TeamAnalytics() {
  const { data } = useDropdownTable({});
  const dropdownTable = data ?? 'loading';

  const now = dayjs().endOf('day');
  const defaultQuery = `endDate=${now.toISOString()}`;

  const returnChartsArrayData = () => {
    const chartItems = [
      {
        title: 'Total Media Interactions',
        plotly: 0,
        queryKey: 'total-media-interactions',
        networkRequest: chartAPIRequests.getMediaInteractionsChart,
      },
      {
        title: 'Issue Response Media Interactions',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'issue-response-media-interactions',
        networkRequest: chartAPIRequests.getIssueResponseMIChart,
      },
      {
        title: 'Team Driven Media Interactions',
        plotly: 0,
        type: chartTypeEnum.barPercentage,
        queryKey: 'team-driven-media-interactions',
        networkRequest: chartAPIRequests.getTeamDrivenMIChart,
      },
      {
        title: 'Institutional Story Media Interactions',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'institutional-story-media-interactions',
        networkRequest: chartAPIRequests.getInstitutionalStoryMIChart,
      },
      {
        title: 'Proactive Client Media Interactions',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'proactive-client-media-interactions',
        networkRequest: chartAPIRequests.getProactiveMIChart,
      },
      {
        title: 'Proactive Media Relations Tactics',
        plotly: 0,
        queryKey: 'proactive-media-relations-tactics',
        networkRequest: chartAPIRequests.getProactiveMRChart,
      },
      {
        title: 'Total Reactive Services',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'total-reactive-services',
        networkRequest: chartAPIRequests.getReactiveServicesChart,
      },
      {
        title: 'Proactive Services',
        plotly: 0,
        queryKey: 'proactive-services',
        networkRequest: chartAPIRequests.getProactiveServicesChart,
      },
      {
        title: 'Issues Complexity',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'issues-complexity',
        networkRequest: chartAPIRequests.getIssueComplexityChart,
      },
      {
        title: 'Total Engagements',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'total-engagements',
        networkRequest: chartAPIRequests.getEngagementsChart,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: 'Total Community Members Engaged',
        plotly: 0,
        type: chartTypeEnum.barVertical,
        queryKey: 'total-community-members-engaged',
        networkRequest: chartAPIRequests.getTotalCommunityMembersChart,
        addOn: AddOnTypes.engagementLog,
      },
      {
        title: `Tone of Interactions`,
        type: chartTypeEnum.stackedBarVertical,
        queryKey: 'tone-of-interactions',
        networkRequest: chartAPIRequests.getToneOfInteractionsChart,
        isYearlyChart: true,
      },
      {
        title: `Media Interactions by Campaign`,
        plotly: 0,
        type: chartTypeEnum.barWide,
        queryKey: 'media-interactions-by-campaign',
        networkRequest: chartAPIRequests.getCampaignsMIChart,
        isYearlyChart: true,
      },
      {
        title: `Services by Campaign`,
        plotly: 0,
        type: chartTypeEnum.barWide,
        queryKey: 'services-by-campaign',
        networkRequest: chartAPIRequests.getCampaignsServiceChart,
        isYearlyChart: true,
      },
      {
        title: `Engagements by Campaign`,
        plotly: 0,
        type: chartTypeEnum.barWide,
        queryKey: 'engagements-by-campaign',
        networkRequest: chartAPIRequests.getCampaignsEngagementChart,
        addOn: AddOnTypes.engagementLog,
        isYearlyChart: true,
      },
    ];

    return chartItems.map((item) => {
      const uiCopy = getTeamUICopyFieldsForItem(teamAnalyticsUICopy.charts, item.queryKey);
      const computedBadges = getBadgesForTeamAnalytics(item.queryKey, { addOn: item.addOn });

      if (process.env.NODE_ENV !== 'production' && computedBadges.some((badge) => !badge?.tooltip?.trim())) {
        console.warn(`Missing tooltip on Team Analytics badge(s) for chart: ${item.queryKey}`);
      }
      if (
        process.env.NODE_ENV !== 'production' &&
        computedBadges.some((badge) => DESCRIPTIVE_BADGE_LABELS.has((badge?.label || '').trim().toLowerCase()))
      ) {
        console.warn(`Descriptive badge still present on Team Analytics chart: ${item.queryKey}`);
      }

      return {
        ...item,
        category: teamSectionByQueryKey[item.queryKey],
        ...uiCopy,
        badges: computedBadges,
        analyticsVariant: 'team',
      };
    });
  };

  const returnTablesDataArray = () => {
    return [];
  };
  window.addEventListener('beforeprint', () => {
    for (let id in Chart.instances) {
      Chart.instances[id].resize(600, 240);
    }
  });
  window.addEventListener('afterprint', () => {
    for (let id in Chart.instances) {
      Chart.instances[id].resize();
    }
  });

  return (
    <div className="analytics-wrapper">
      {dropdownTable !== 'loading' ? (
        <div className={'analytics-charts'}>
          <AnalyticsTable
            chartDataArray={returnChartsArrayData()}
            tableDataArray={returnTablesDataArray()}
            query={defaultQuery}
            sectionDisplayOrder={teamSectionDisplayOrder}
          ></AnalyticsTable>
        </div>
      ) : (
        <Spinner animation="border"></Spinner>
      )}
    </div>
  );
}

export default TeamAnalytics;
